import type {
  ChapterCharacterLayer,
  CharacterEntity,
  CharacterLayerDetectionRow,
  CharacterLayerDetectorAdapter,
  CharacterLayerDetectorOutput,
  CharacterLayerNote,
  CharacterOccurrence,
  CharacterOccurrenceLocator,
} from '../types/characterLayerTypes'
import {
  getCharacterFigureKind,
  getCharacterOccurrenceReferentIds,
  isJointCharacterOccurrence,
} from './characterLayerHelpers'

export interface CharacterLayerDetectorMergeReport {
  added_character_ids: string[]
  matched_character_ids: string[]
  suppressed_character_ids: string[]
  added_occurrence_ids: string[]
  skipped_occurrence_ids: string[]
  added_note_ids: string[]
}

const nowIso = (): string => new Date().toISOString()

const stableSuffix = (): string => Math.random().toString(36).slice(2, 10)

const createId = (prefix: string): string => `${prefix}_${Date.now().toString(36)}_${stableSuffix()}`

const normalizeValue = (value?: string): string => (
  (value || '').trim().toLowerCase().replace(/\s+/g, ' ')
)

const uniqueStrings = (values: string[]): string[] => (
  Array.from(new Set(values.map(value => value.trim().replace(/\s+/g, ' ')).filter(Boolean)))
)

const characterKeys = (character: Pick<CharacterEntity, 'display_name' | 'aliases'>): string[] => (
  uniqueStrings([character.display_name, ...(character.aliases || [])])
    .map(normalizeValue)
    .filter(Boolean)
)

const sanitizeLocator = (locator: CharacterOccurrenceLocator): CharacterOccurrenceLocator => {
  const base: CharacterOccurrenceLocator = {
    witnessId: locator.witnessId,
    rowId: locator.rowId,
  }
  if (
    typeof locator.localStart === 'number' &&
    typeof locator.localEnd === 'number' &&
    Number.isFinite(locator.localStart) &&
    Number.isFinite(locator.localEnd) &&
    locator.localEnd > locator.localStart
  ) {
    base.localStart = Math.max(0, Math.floor(locator.localStart))
    base.localEnd = Math.max(base.localStart, Math.floor(locator.localEnd))
  }
  return base
}

const occurrenceIdentityKey = (occurrence: Pick<CharacterOccurrence, 'character_id' | 'locator' | 'role_labels'>): string => (
  [
    occurrence.character_id,
    occurrence.locator.witnessId,
    occurrence.locator.rowId,
    occurrence.locator.localStart ?? '',
    occurrence.locator.localEnd ?? '',
    uniqueStrings(occurrence.role_labels || []).map(normalizeValue).sort().join('|'),
  ].join('::')
)

const noteIdentityKey = (note: CharacterLayerNote): string => (
  `${JSON.stringify(note.anchor)}::${normalizeValue(note.text)}`
)

type SpanLocator = {
  witnessId: string
  rowId: string
  localStart: number
  localEnd: number
}

type MergePolicy = 'exact' | 'contained-span'

const isFiniteSpanLocator = (locator: CharacterOccurrenceLocator): locator is SpanLocator => (
  typeof locator.localStart === 'number' &&
  typeof locator.localEnd === 'number' &&
  Number.isFinite(locator.localStart) &&
  Number.isFinite(locator.localEnd) &&
  locator.localEnd > locator.localStart
)

const spansShareRow = (left: SpanLocator, right: SpanLocator): boolean => (
  left.witnessId === right.witnessId && left.rowId === right.rowId
)

const spanContains = (outer: SpanLocator, inner: SpanLocator): boolean => (
  spansShareRow(outer, inner) &&
  outer.localStart <= inner.localStart &&
  outer.localEnd >= inner.localEnd
)

const spansContainOneAnother = (left: SpanLocator, right: SpanLocator): boolean => (
  spanContains(left, right) || spanContains(right, left)
)

const spanLength = (locator: SpanLocator): number => locator.localEnd - locator.localStart

const roleIdentityKey = (roles: readonly string[] = []): string => (
  uniqueStrings([...roles]).map(normalizeValue).sort().join('|')
)

const sameOccurrenceMergeScope = (left: CharacterOccurrence, right: CharacterOccurrence): boolean => (
  getCharacterOccurrenceReferentIds(left).some(characterId => (
    getCharacterOccurrenceReferentIds(right).includes(characterId)
  )) &&
  roleIdentityKey(left.role_labels) === roleIdentityKey(right.role_labels)
)

const getNoteMetadataValue = (note: CharacterLayerNote, key: string): unknown => (
  note.metadata && Object.prototype.hasOwnProperty.call(note.metadata, key) ? note.metadata[key] : undefined
)

const getLayerNoteCharacterId = (note: CharacterLayerNote): string => {
  const value = getNoteMetadataValue(note, 'character_id')
  return typeof value === 'string' ? value.trim() : ''
}

const getLayerNoteKind = (note: CharacterLayerNote): string => {
  const value = getNoteMetadataValue(note, 'character_index_layer')
  return typeof value === 'string' ? value.trim() : ''
}

const getLayerNoteSourceField = (note: CharacterLayerNote): string => {
  const value = getNoteMetadataValue(note, 'source_field')
  return typeof value === 'string' ? value.trim() : ''
}

const getLayerNoteDevices = (note: CharacterLayerNote): string[] => {
  const value = getNoteMetadataValue(note, 'devices')
  return Array.isArray(value)
    ? value.map(item => typeof item === 'string' ? item.trim() : '').filter(Boolean)
    : []
}

const getLayerNoteSpan = (note: CharacterLayerNote): SpanLocator | null => {
  if (note.anchor.kind !== 'span') {
    return null
  }
  const locator = sanitizeLocator(note.anchor.locator)
  return isFiniteSpanLocator(locator) ? locator : null
}

const sameLayerNoteMergeScope = (left: CharacterLayerNote, right: CharacterLayerNote): boolean => {
  const leftLayer = getLayerNoteKind(left)
  const rightLayer = getLayerNoteKind(right)
  if (!leftLayer || leftLayer !== rightLayer) {
    return false
  }
  const leftCharacterId = getLayerNoteCharacterId(left)
  const rightCharacterId = getLayerNoteCharacterId(right)
  return Boolean(leftCharacterId && leftCharacterId === rightCharacterId)
}

const stripArabicLeadingConjunctions = (value: string): string => (
  value.trim().replace(/^[وف]+\s*/u, '').trim()
)

const hasPredicationCue = (value: string): boolean => (
  /(?:^|\s)(?:أنا|أنت|أنتِ|هو|هي|إنّي|إني|إنّك|إنك|إنّه|إنه|كان|كانت|ليس|ليست|له|لها|لهم|عندي|عنده)/u.test(stripArabicLeadingConjunctions(value))
)

const noteUsesAnyDevice = (note: CharacterLayerNote, devices: readonly string[]): boolean => {
  const wanted = new Set(devices)
  return getLayerNoteDevices(note).some(device => wanted.has(device))
}

const chooseCanonicalOccurrence = (
  existing: CharacterOccurrence,
  incoming: CharacterOccurrence,
): CharacterOccurrence => {
  if (isJointCharacterOccurrence(existing)) {
    return existing
  }
  const existingSpan = isFiniteSpanLocator(existing.locator) ? existing.locator : null
  const incomingSpan = isFiniteSpanLocator(incoming.locator) ? incoming.locator : null
  if (!existingSpan || !incomingSpan || !spansContainOneAnother(existingSpan, incomingSpan)) {
    return existing
  }
  return spanLength(incomingSpan) < spanLength(existingSpan) ? incoming : existing
}

const chooseCanonicalLayerNote = (
  existing: CharacterLayerNote,
  incoming: CharacterLayerNote,
): CharacterLayerNote => {
  const existingSpan = getLayerNoteSpan(existing)
  const incomingSpan = getLayerNoteSpan(incoming)
  if (!existingSpan || !incomingSpan || !spansContainOneAnother(existingSpan, incomingSpan)) {
    return existing
  }
  const layer = getLayerNoteKind(existing)
  if (layer === 'focalization') {
    const existingIsMarker = getLayerNoteSourceField(existing) === 'marker'
    const incomingIsMarker = getLayerNoteSourceField(incoming) === 'marker'
    if (existingIsMarker !== incomingIsMarker) {
      return incomingIsMarker ? incoming : existing
    }
    return spanLength(incomingSpan) < spanLength(existingSpan) ? incoming : existing
  }

  const existingStripped = normalizeValue(stripArabicLeadingConjunctions(existing.text))
  const incomingStripped = normalizeValue(stripArabicLeadingConjunctions(incoming.text))
  if (existingStripped && existingStripped === incomingStripped) {
    return spanLength(incomingSpan) < spanLength(existingSpan) ? incoming : existing
  }

  if (
    noteUsesAnyDevice(existing, ['manner_pp', 'maf3ul_liajlih', 'hal', 'tamyiz']) ||
    noteUsesAnyDevice(incoming, ['manner_pp', 'maf3ul_liajlih', 'hal', 'tamyiz'])
  ) {
    return spanLength(incomingSpan) < spanLength(existingSpan) ? incoming : existing
  }

  if (
    noteUsesAnyDevice(existing, ['khabar', 'kana_pred', 'kana_lahu', 'negated_trait']) ||
    noteUsesAnyDevice(incoming, ['khabar', 'kana_pred', 'kana_lahu', 'negated_trait'])
  ) {
    const existingCue = hasPredicationCue(existing.text)
    const incomingCue = hasPredicationCue(incoming.text)
    if (existingCue !== incomingCue) {
      return incomingCue ? incoming : existing
    }
  }

  return spanLength(incomingSpan) < spanLength(existingSpan) ? incoming : existing
}

const buildOccurrenceObservation = (
  occurrence: CharacterOccurrence,
  detectorId: string,
  sourceRunId?: string,
): Record<string, unknown> => ({
  kind: 'occurrence',
  occurrence_id: occurrence.occurrence_id,
  detector_id: occurrence.metadata?.detector_id || detectorId,
  ...(occurrence.source_run_id || sourceRunId ? { source_run_id: occurrence.source_run_id || sourceRunId } : {}),
  locator: occurrence.locator,
  character_id: occurrence.character_id,
  ...(isJointCharacterOccurrence(occurrence)
    ? { referent_character_ids: getCharacterOccurrenceReferentIds(occurrence) }
    : {}),
  role_labels: occurrence.role_labels,
  ...(occurrence.label ? { label: occurrence.label } : {}),
  ...(occurrence.summary ? { summary: occurrence.summary } : {}),
  ...(occurrence.metadata?.output_contract ? { output_contract: occurrence.metadata.output_contract } : {}),
  ...(occurrence.metadata?.source_id ? { source_id: occurrence.metadata.source_id } : {}),
  ...(occurrence.metadata?.mention_id ? { mention_id: occurrence.metadata.mention_id } : {}),
})

const buildNoteObservation = (
  note: CharacterLayerNote,
  detectorId: string,
  sourceRunId?: string,
): Record<string, unknown> => ({
  kind: 'note',
  note_id: note.note_id,
  detector_id: note.metadata?.detector_id || detectorId,
  ...(note.metadata?.source_run_id || sourceRunId ? { source_run_id: note.metadata?.source_run_id || sourceRunId } : {}),
  anchor: note.anchor,
  text: note.text,
  ...(note.metadata?.output_contract ? { output_contract: note.metadata.output_contract } : {}),
  ...(note.metadata?.character_id ? { character_id: note.metadata.character_id } : {}),
  ...(note.metadata?.character_index_layer ? { character_index_layer: note.metadata.character_index_layer } : {}),
  ...(note.metadata?.source_id ? { source_id: note.metadata.source_id } : {}),
  ...(note.metadata?.source_field ? { source_field: note.metadata.source_field } : {}),
  ...(note.metadata?.class ? { class: note.metadata.class } : {}),
  ...(note.metadata?.devices ? { devices: note.metadata.devices } : {}),
})

const observationKey = (observation: Record<string, unknown>): string => JSON.stringify(observation)

const mergeObservationMetadata = ({
  baseMetadata,
  canonicalizationPolicy,
  detectorId,
  incomingObservation,
  sourceRunId,
  timestamp,
}: {
  baseMetadata?: Record<string, any>
  canonicalizationPolicy: MergePolicy
  detectorId: string
  incomingObservation: Record<string, unknown>
  sourceRunId?: string
  timestamp: string
}): Record<string, any> => {
  const existingObservations = Array.isArray(baseMetadata?.observations)
    ? baseMetadata!.observations.filter(item => item && typeof item === 'object') as Record<string, unknown>[]
    : []
  const observationMap = new Map(existingObservations.map(observation => [observationKey(observation), observation]))
  observationMap.set(observationKey(incomingObservation), incomingObservation)
  const observations = Array.from(observationMap.values())
  return {
    ...(baseMetadata || {}),
    detector_id: baseMetadata?.detector_id || detectorId,
    ...(sourceRunId ? { source_run_id: baseMetadata?.source_run_id || sourceRunId } : {}),
    observations,
    canonicalization: {
      ...(baseMetadata?.canonicalization || {}),
      policy: canonicalizationPolicy,
      variant_count: observations.length,
      updated_at: timestamp,
    },
  }
}

const withMergedOccurrenceObservation = ({
  canonical,
  detectorId,
  existing,
  incoming,
  policy,
  sourceRunId,
  timestamp,
}: {
  canonical: CharacterOccurrence
  existing: CharacterOccurrence
  incoming: CharacterOccurrence
  policy: MergePolicy
  detectorId: string
  sourceRunId?: string
  timestamp: string
}): CharacterOccurrence => {
  const baseObservation = buildOccurrenceObservation(existing, detectorId, sourceRunId)
  const incomingObservation = buildOccurrenceObservation(incoming, detectorId, sourceRunId)
  const firstPassMetadata = mergeObservationMetadata({
    baseMetadata: canonical.metadata,
    canonicalizationPolicy: policy,
    detectorId,
    incomingObservation: baseObservation,
    sourceRunId,
    timestamp,
  })
  return {
    ...canonical,
    occurrence_id: existing.occurrence_id,
    source_run_id: existing.source_run_id,
    created_at: existing.created_at,
    updated_at: timestamp,
    metadata: mergeObservationMetadata({
      baseMetadata: firstPassMetadata,
      canonicalizationPolicy: policy,
      detectorId,
      incomingObservation,
      sourceRunId,
      timestamp,
    }),
  }
}

const withMergedNoteObservation = ({
  canonical,
  detectorId,
  existing,
  incoming,
  policy,
  sourceRunId,
  timestamp,
}: {
  canonical: CharacterLayerNote
  existing: CharacterLayerNote
  incoming: CharacterLayerNote
  policy: MergePolicy
  detectorId: string
  sourceRunId?: string
  timestamp: string
}): CharacterLayerNote => {
  const baseObservation = buildNoteObservation(existing, detectorId, sourceRunId)
  const incomingObservation = buildNoteObservation(incoming, detectorId, sourceRunId)
  const canonicalMetadata = {
    ...(existing.metadata || {}),
    ...(canonical.metadata || {}),
  }
  const firstPassMetadata = mergeObservationMetadata({
    baseMetadata: canonicalMetadata,
    canonicalizationPolicy: policy,
    detectorId,
    incomingObservation: baseObservation,
    sourceRunId,
    timestamp,
  })
  return {
    ...canonical,
    note_id: existing.note_id,
    created_at: existing.created_at,
    updated_at: timestamp,
    metadata: mergeObservationMetadata({
      baseMetadata: firstPassMetadata,
      canonicalizationPolicy: policy,
      detectorId,
      incomingObservation,
      sourceRunId,
      timestamp,
    }),
  }
}

export const mergeCharacterLayerDetectorOutput = ({
  existingLayer,
  output,
  detectorId,
  sourceRunId,
  createdAt,
}: {
  existingLayer: ChapterCharacterLayer
  output: CharacterLayerDetectorOutput
  detectorId: string
  sourceRunId?: string
  createdAt?: string
}): { layer: ChapterCharacterLayer; report: CharacterLayerDetectorMergeReport } => {
  const timestamp = createdAt || nowIso()
  const nextCharacters = [...existingLayer.characters]
  const nextOccurrences = [...existingLayer.occurrences]
  const nextNotes = [...existingLayer.notes]
  const report: CharacterLayerDetectorMergeReport = {
    added_character_ids: [],
    matched_character_ids: [],
    suppressed_character_ids: [],
    added_occurrence_ids: [],
    skipped_occurrence_ids: [],
    added_note_ids: [],
  }

  const activeCharacterByKey = new Map<string, CharacterEntity>()
  const rejectedCharacterByKey = new Map<string, CharacterEntity>()
  const characterById = new Map<string, CharacterEntity>()
  nextCharacters.forEach(character => {
    characterById.set(character.character_id, character)
    characterKeys(character).forEach(key => {
      if (character.status === 'rejected') {
        rejectedCharacterByKey.set(key, character)
      } else {
        activeCharacterByKey.set(key, character)
      }
    })
  })

  const incomingCharacterIdToResolvedId = new Map<string, string | null>()
  output.characters.forEach(incomingCharacter => {
    const keys = characterKeys(incomingCharacter)
    const idMatch = incomingCharacter.character_id
      ? characterById.get(incomingCharacter.character_id)
      : undefined
    const rejectedMatch = idMatch?.status === 'rejected'
      ? idMatch
      : keys.map(key => rejectedCharacterByKey.get(key)).find(Boolean)
    if (rejectedMatch) {
      incomingCharacterIdToResolvedId.set(incomingCharacter.character_id, null)
      report.suppressed_character_ids.push(incomingCharacter.character_id)
      return
    }

    const activeMatch = idMatch || keys.map(key => activeCharacterByKey.get(key)).find(Boolean)
    if (activeMatch) {
      const incomingKind = getCharacterFigureKind(incomingCharacter)
      if (!activeMatch.kind && incomingKind) {
        const activeIndex = nextCharacters.findIndex(character => character.character_id === activeMatch.character_id)
        if (activeIndex >= 0) {
          const promotedCharacter: CharacterEntity = {
            ...activeMatch,
            kind: incomingKind,
            metadata: {
              ...(activeMatch.metadata || {}),
              figure_kind: incomingKind,
            },
            updated_at: timestamp,
          }
          nextCharacters[activeIndex] = promotedCharacter
          characterKeys(promotedCharacter).forEach(key => activeCharacterByKey.set(key, promotedCharacter))
        }
      }
      incomingCharacterIdToResolvedId.set(incomingCharacter.character_id, activeMatch.character_id)
      report.matched_character_ids.push(activeMatch.character_id)
      return
    }

    const characterId = incomingCharacter.character_id || createId('character')
    const character: CharacterEntity = {
      ...incomingCharacter,
      character_id: characterId,
      aliases: uniqueStrings(incomingCharacter.aliases || []),
      status: incomingCharacter.status || 'auto',
      origin: incomingCharacter.origin || 'llm',
      source_run_id: incomingCharacter.source_run_id || sourceRunId,
      metadata: {
        ...(incomingCharacter.metadata || {}),
        detector_id: detectorId,
      },
      created_at: incomingCharacter.created_at || timestamp,
      updated_at: timestamp,
    }
    nextCharacters.push(character)
    characterById.set(character.character_id, character)
    characterKeys(character).forEach(key => activeCharacterByKey.set(key, character))
    incomingCharacterIdToResolvedId.set(incomingCharacter.character_id, character.character_id)
    report.added_character_ids.push(character.character_id)
  })

  const existingOccurrenceIndexByKey = new Map(nextOccurrences.map((occurrence, index) => [occurrenceIdentityKey(occurrence), index]))
  output.occurrences.forEach(incomingOccurrence => {
    const resolvedCharacterId = incomingCharacterIdToResolvedId.has(incomingOccurrence.character_id)
      ? incomingCharacterIdToResolvedId.get(incomingOccurrence.character_id)
      : incomingOccurrence.character_id
    if (!resolvedCharacterId) {
      report.skipped_occurrence_ids.push(incomingOccurrence.occurrence_id)
      return
    }
    const occurrence: CharacterOccurrence = {
      ...incomingOccurrence,
      occurrence_id: incomingOccurrence.occurrence_id || createId('character_occurrence'),
      character_id: resolvedCharacterId,
      locator: sanitizeLocator(incomingOccurrence.locator),
      role_labels: uniqueStrings(incomingOccurrence.role_labels || []),
      status: incomingOccurrence.status || 'auto',
      origin: incomingOccurrence.origin || 'llm',
      source_run_id: incomingOccurrence.source_run_id || sourceRunId,
      metadata: {
        ...(incomingOccurrence.metadata || {}),
        detector_id: detectorId,
      },
      created_at: incomingOccurrence.created_at || timestamp,
      updated_at: timestamp,
    }
    const identityKey = occurrenceIdentityKey(occurrence)
    const exactOccurrenceIndex = existingOccurrenceIndexByKey.get(identityKey)
    if (typeof exactOccurrenceIndex === 'number') {
      nextOccurrences[exactOccurrenceIndex] = withMergedOccurrenceObservation({
        canonical: nextOccurrences[exactOccurrenceIndex],
        detectorId,
        existing: nextOccurrences[exactOccurrenceIndex],
        incoming: occurrence,
        policy: 'exact',
        sourceRunId,
        timestamp,
      })
      report.skipped_occurrence_ids.push(occurrence.occurrence_id)
      return
    }

    const containedOccurrenceIndex = nextOccurrences.findIndex(existingOccurrence => {
      if (!sameOccurrenceMergeScope(existingOccurrence, occurrence)) {
        return false
      }
      const existingSpan = isFiniteSpanLocator(existingOccurrence.locator) ? existingOccurrence.locator : null
      const incomingSpan = isFiniteSpanLocator(occurrence.locator) ? occurrence.locator : null
      return Boolean(existingSpan && incomingSpan && spansContainOneAnother(existingSpan, incomingSpan))
    })
    if (containedOccurrenceIndex >= 0) {
      const existingOccurrence = nextOccurrences[containedOccurrenceIndex]
      const canonical = chooseCanonicalOccurrence(existingOccurrence, occurrence)
      nextOccurrences[containedOccurrenceIndex] = withMergedOccurrenceObservation({
        canonical,
        detectorId,
        existing: existingOccurrence,
        incoming: occurrence,
        policy: 'contained-span',
        sourceRunId,
        timestamp,
      })
      existingOccurrenceIndexByKey.set(occurrenceIdentityKey(nextOccurrences[containedOccurrenceIndex]), containedOccurrenceIndex)
      report.skipped_occurrence_ids.push(occurrence.occurrence_id)
      return
    }

    existingOccurrenceIndexByKey.set(identityKey, nextOccurrences.length)
    nextOccurrences.push(occurrence)
    report.added_occurrence_ids.push(occurrence.occurrence_id)
  })

  const existingNoteIndexByKey = new Map(nextNotes.map((note, index) => [noteIdentityKey(note), index]))
  const incomingNotes = output.notes || []
  incomingNotes.forEach(incomingNote => {
    const note: CharacterLayerNote = {
      ...incomingNote,
      note_id: incomingNote.note_id || createId('character_note'),
      metadata: {
        ...(incomingNote.metadata || {}),
        detector_id: incomingNote.metadata?.detector_id || detectorId,
        ...(sourceRunId ? { source_run_id: incomingNote.metadata?.source_run_id || sourceRunId } : {}),
      },
      created_at: incomingNote.created_at || timestamp,
      updated_at: timestamp,
    }
    const identityKey = noteIdentityKey(note)
    const exactNoteIndex = existingNoteIndexByKey.get(identityKey)
    if (typeof exactNoteIndex === 'number') {
      nextNotes[exactNoteIndex] = withMergedNoteObservation({
        canonical: nextNotes[exactNoteIndex],
        detectorId,
        existing: nextNotes[exactNoteIndex],
        incoming: note,
        policy: 'exact',
        sourceRunId,
        timestamp,
      })
      return
    }

    const noteSpan = getLayerNoteSpan(note)
    const containedNoteIndex = noteSpan
      ? nextNotes.findIndex(existingNote => {
        const existingSpan = getLayerNoteSpan(existingNote)
        return Boolean(
          existingSpan &&
          sameLayerNoteMergeScope(existingNote, note) &&
          spansContainOneAnother(existingSpan, noteSpan)
        )
      })
      : -1
    if (containedNoteIndex >= 0) {
      const existingNote = nextNotes[containedNoteIndex]
      const canonical = chooseCanonicalLayerNote(existingNote, note)
      nextNotes[containedNoteIndex] = withMergedNoteObservation({
        canonical,
        detectorId,
        existing: existingNote,
        incoming: note,
        policy: 'contained-span',
        sourceRunId,
        timestamp,
      })
      existingNoteIndexByKey.set(noteIdentityKey(nextNotes[containedNoteIndex]), containedNoteIndex)
      return
    }

    existingNoteIndexByKey.set(identityKey, nextNotes.length)
    nextNotes.push(note)
    report.added_note_ids.push(note.note_id)
  })

  return {
    layer: {
      ...existingLayer,
      characters: nextCharacters,
      occurrences: nextOccurrences,
      notes: nextNotes,
      metadata: {
        ...(existingLayer.metadata || {}),
        last_character_detector_id: detectorId,
        ...(sourceRunId ? { last_character_detector_run_id: sourceRunId } : {}),
      },
      updated_at: timestamp,
    },
    report,
  }
}

export const createStaticCharacterLayerDetectorAdapter = ({
  id,
  label,
  output,
}: {
  id: string
  label: string
  output: CharacterLayerDetectorOutput
}): CharacterLayerDetectorAdapter => ({
  id,
  label,
  kind: 'manual-import',
  detect: async () => output,
})

const escapeRegExp = (value: string): string => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

const hasLatinBoundary = (value: string | undefined): boolean => Boolean(value && /[A-Za-z0-9_]/.test(value))

const findNameMatches = (text: string, name: string): Array<{ localStart: number; localEnd: number }> => {
  const trimmedName = name.trim()
  if (!text || !trimmedName) {
    return []
  }
  const pattern = new RegExp(escapeRegExp(trimmedName), 'giu')
  const matches: Array<{ localStart: number; localEnd: number }> = []
  let match: RegExpExecArray | null
  while ((match = pattern.exec(text)) !== null) {
    const localStart = match.index
    const localEnd = localStart + match[0].length
    const before = localStart > 0 ? text[localStart - 1] : ''
    const after = localEnd < text.length ? text[localEnd] : ''
    if (!hasLatinBoundary(before) && !hasLatinBoundary(after)) {
      matches.push({ localStart, localEnd })
    }
    if (match[0].length === 0) {
      pattern.lastIndex += 1
    }
  }
  return matches
}

const isDetectorCandidateCharacter = (character: CharacterEntity): boolean => (
  character.status === 'confirmed' || character.status === 'edited'
)

const buildNameSweepOccurrences = ({
  characters,
  rows,
}: {
  characters: CharacterEntity[]
  rows: CharacterLayerDetectionRow[]
}): CharacterOccurrence[] => {
  const timestamp = nowIso()
  const occurrences: CharacterOccurrence[] = []
  characters.forEach(character => {
    const names = uniqueStrings([character.display_name, ...(character.aliases || [])])
    rows.forEach(row => {
      names.forEach(name => {
        findNameMatches(row.text, name).forEach(match => {
          occurrences.push({
            occurrence_id: createId('character_occurrence'),
            character_id: character.character_id,
            locator: {
              witnessId: row.witnessId,
              rowId: row.rowId,
              localStart: match.localStart,
              localEnd: match.localEnd,
            },
            role_labels: ['mentioned'],
            label: name,
            status: 'auto',
            origin: 'derived',
            confidence: 0.62,
            metadata: {
              detector_rule: 'name-alias-sweep',
              witness_sigla: row.witnessSigla,
              row_label: row.rowLabel,
            },
            created_at: timestamp,
            updated_at: timestamp,
          })
        })
      })
    })
  })
  return occurrences
}

export const createNameSweepCharacterLayerDetectorAdapter = (): CharacterLayerDetectorAdapter => ({
  id: 'name-alias-sweep',
  label: 'Name/alias sweep',
  kind: 'rule-based',
  detect: async request => {
    const characters = (request.existingLayer?.characters || []).filter(isDetectorCandidateCharacter)
    return {
      characters: [],
      occurrences: buildNameSweepOccurrences({
        characters,
        rows: request.rows,
      }),
      metadata: {
        detector_id: 'name-alias-sweep',
        row_count: request.rows.length,
        character_count: characters.length,
      },
    }
  },
})
